/**
 * La préférence d'apparence de l'espace tuteur — Clair, Sombre ou Automatique —, gardée sur
 * l'appareil.
 *
 * Le même magasin que celui du parent et de l'élève (`createParentAppearanceStore`), sur sa propre
 * clé : **le tuteur s'ouvre en Clair** (décidé le 28/10, la maquette de la feuille de profil), et
 * garde son choix dans « Apparence ». Un appareil partagé entre un tuteur et un élève retrouve chacun
 * le sien.
 */
import { useEffect, useSyncExternalStore } from "react"

import { getFromStorage, setToStorage } from "../../../../data/utility/asyncStorageUtils"
import { createParentAppearanceStore, type ParentAppearancePreference } from "./parentAppearancePreference"

type TutorAppearancePreference = ParentAppearancePreference

/** Clé explicite, distincte de celles du parent et de l'élève. */
const TUTOR_APPEARANCE_STORAGE_KEY = "masteur.tutorSpace.appearance"

/** Le défaut du tuteur : Clair. */
const TUTOR_APPEARANCE_FALLBACK: TutorAppearancePreference = "light"

const store = createParentAppearanceStore(
    { read: getFromStorage, write: setToStorage },
    { storageKey: TUTOR_APPEARANCE_STORAGE_KEY, fallback: TUTOR_APPEARANCE_FALLBACK }
)

/** Le geste de la section « Apparence » de la feuille de profil. */
export const setTutorAppearancePreference = store.set

/** La préférence courante, relue à chaque changement ; la lecture de l'appareil part au premier lecteur. */
export function useTutorAppearancePreference(): TutorAppearancePreference {
    const preference = useSyncExternalStore(store.subscribe, store.get, store.get)

    useEffect(() => {
        void store.hydrate()
    }, [])

    return preference
}
